// Weekly route schedules per truck (multi-route days are separate entries).

import type { DayOfWeek, OrganizationWeekSchedule, TruckWeekSchedule } from '../../types/routeSchedule.type';
import { DEMO_ROUTES, type DemoWeekday } from '../core';
import { listTrucks } from './fleet';
import { routeRecordById, serializeRoute } from './network';

interface ScheduleEntry {
  truck_id: string;
  day: DayOfWeek;
  route_id: string;
  seq: number;
}

export const DAYS_MONDAY_FIRST: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const FROM_CORE: Record<DemoWeekday, DayOfWeek> = {
  sun: 'sunday',
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
};

let entries: ScheduleEntry[] = [];

export function resetSchedule(): void {
  entries = [];
  DEMO_ROUTES.forEach((route) => {
    route.schedule.days.forEach((d) => {
      const day = FROM_CORE[d];
      const seq = entries.filter((e) => e.truck_id === route.truck_id && e.day === day).length + 1;
      entries.push({ truck_id: route.truck_id, day, route_id: route.id, seq });
    });
  });
}

export function isDayOfWeek(value: string): value is DayOfWeek {
  return (DAYS_MONDAY_FIRST as string[]).includes(value);
}

export function truckSchedule(truckId: string): TruckWeekSchedule {
  const days = entries
    .filter((e) => e.truck_id === truckId)
    .filter((e) => {
      const route = routeRecordById(e.route_id);
      return route && route.status !== 'archived';
    })
    .sort((a, b) => DAYS_MONDAY_FIRST.indexOf(a.day) - DAYS_MONDAY_FIRST.indexOf(b.day) || a.seq - b.seq)
    .map((e) => ({ day_of_week: e.day, route: serializeRoute(routeRecordById(e.route_id)!) }));
  return { truck_id: truckId, days };
}

export function organizationSchedule(orgId: string): OrganizationWeekSchedule {
  return { schedules: listTrucks(orgId).map((t) => truckSchedule(t.id)) };
}

/** PUT …/day/{dow}: the given ordered routes replace that weekday for the truck. */
export function setScheduleDay(truckId: string, day: DayOfWeek, routeIds: string[]): TruckWeekSchedule {
  entries = entries.filter((e) => !(e.truck_id === truckId && e.day === day));
  routeIds
    .filter((id) => routeRecordById(id))
    .forEach((routeId, i) => entries.push({ truck_id: truckId, day, route_id: routeId, seq: i + 1 }));
  return truckSchedule(truckId);
}

export function clearScheduleDay(truckId: string, day: DayOfWeek): void {
  entries = entries.filter((e) => !(e.truck_id === truckId && e.day === day));
}

/** Weekdays a route is scheduled on (for its detail page). */
export function routeDays(routeId: string): DayOfWeek[] {
  return Array.from(new Set(entries.filter((e) => e.route_id === routeId).map((e) => e.day)));
}
